import { useState } from "react"
import { Link, useParams, useNavigate } from "@tanstack/react-router"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { authClient } from "../lib/auth-client"
import { fetcher } from "../lib/api"
import { Button, Card, Input, Label, Dialog, DialogTitle, Textarea, Skeleton } from "../components/ui"
import { useToast } from "../components/toast"
import { Shell } from "../components/shell"
import { Plus, Settings, Zap, FolderOpen, Kanban, Users, ArrowRight } from "lucide-react"

/** Workspace detail page. Shows projects within a workspace with cards, plus quick links to settings, members, and billing. */
export default function WorkspacePage() {
  const { workspaceId } = useParams({ from: "/workspaces/$workspaceId" })
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { data: session } = authClient.useSession()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [editName, setEditName] = useState("")
  const [editDescription, setEditDescription] = useState("")

  const { data: workspace, isLoading } = useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: () => fetcher<{ id: string; name: string; description: string | null; role: string }>(`/api/workspaces/${workspaceId}`),
    enabled: !!session,
  })

  const { data: projects } = useQuery({
    queryKey: ["projects", workspaceId],
    queryFn: () => fetcher<{ id: string; name: string; description: string | null; status: string | null }[]>(`/api/projects?workspaceId=${workspaceId}`),
    enabled: !!workspace,
  })

  const isAdmin = workspace?.role === "owner" || workspace?.role === "admin"

  const { data: billing } = useQuery({
    queryKey: ["billing", workspaceId],
    queryFn: () => fetcher<{ subscription: { plan: string; status: string } | null }>(`/api/billing/workspace/${workspaceId}`),
    enabled: !!workspace && isAdmin,
  })

  const create = useMutation({
    mutationFn: (body: { workspaceId: string; name: string; description?: string }) =>
      fetcher("/api/projects", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", workspaceId] })
      toast("Project created", "success")
      setOpen(false)
      setName("")
      setDescription("")
    },
    onError: (err: any) => toast(err.message || "Failed to create project", "error"),
  })

  const updateWorkspace = useMutation({
    mutationFn: (body: { name: string; description?: string }) =>
      fetcher(`/api/workspaces/${workspaceId}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspace", workspaceId] })
      queryClient.invalidateQueries({ queryKey: ["workspaces"] })
      toast("Workspace updated", "success")
      setSettingsOpen(false)
    },
    onError: (err: any) => toast(err.message || "Failed to update workspace", "error"),
  })

  const deleteWorkspace = useMutation({
    mutationFn: () => fetcher(`/api/workspaces/${workspaceId}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspaces"] })
      toast("Workspace deleted", "success")
      navigate({ to: "/" })
    },
    onError: (err: any) => toast(err.message || "Failed to delete workspace", "error"),
  })

  if (!session) {
    navigate({ to: "/login" })
    return null
  }
  if (isLoading || !workspace) return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="w-64 space-y-3">
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  )

  return (
    <Shell
      title={workspace.name}
      back={{ to: "/", label: "Workspaces" }}
      tabs={
        <div className="flex gap-4 text-sm">
          <span className="border-b-2 border-primary pb-2 font-medium">Projects</span>
          <Link to="/workspaces/$workspaceId/members" params={{ workspaceId }} className="pb-2 text-muted-foreground hover:text-foreground">
            Members
          </Link>
          <Link to="/workspaces/$workspaceId/billing" params={{ workspaceId }} className="pb-2 text-muted-foreground hover:text-foreground">
            Billing
          </Link>
          {isAdmin && (
            <Link to="/workspaces/$workspaceId/audit" params={{ workspaceId }} className="pb-2 text-muted-foreground hover:text-foreground">
              Audit log
            </Link>
          )}
          {isAdmin && (
            <Link to="/workspaces/$workspaceId/webhooks" params={{ workspaceId }} className="pb-2 text-muted-foreground hover:text-foreground">
              Webhooks
            </Link>
          )}
        </div>
      }
      actions={
        isAdmin && (
          <>
            <Button size="sm" variant="outline" onClick={() => { setEditName(workspace.name); setEditDescription(workspace.description || ""); setSettingsOpen(true) }}>
              <Settings className="mr-1 h-4 w-4" /> Settings
            </Button>
            <Button size="sm" onClick={() => setOpen(true)}>
              <Plus className="mr-1 h-4 w-4" /> New project
            </Button>
          </>
        )
      }
    >
      <main className="mx-auto max-w-6xl p-6">
        <h2 className="mb-4 text-xl font-semibold tracking-tight">Projects</h2>

        {isAdmin && (!billing?.subscription || billing.subscription.plan === "free") && (
          <div className="mb-6 rounded-xl border border-primary/20 bg-gradient-to-r from-primary/10 to-primary/5 p-4 shadow-sm">
            <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                  <Zap className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Upgrade to Pro</h3>
                  <p className="text-sm text-muted-foreground">Get unlimited projects, members, and more with Pro.</p>
                </div>
              </div>
              <Link to="/workspaces/$workspaceId/billing" params={{ workspaceId }}>
                <Button size="sm">Upgrade now</Button>
              </Link>
            </div>
          </div>
        )}

        {projects?.length ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((p) => (
              <Link key={p.id} to="/workspaces/$workspaceId/projects/$projectId" params={{ workspaceId, projectId: p.id }} className="block h-full w-full">
                <Card className="group relative flex h-full min-h-[11rem] w-full min-w-[16rem] cursor-pointer flex-col overflow-hidden rounded-2xl border border-border bg-card p-5 shadow-sm transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-md">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary shadow-sm transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                      <FolderOpen className="h-6 w-6" />
                    </div>
                    <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium capitalize text-muted-foreground">
                      {p.status || "active"}
                    </span>
                  </div>
                  <div className="mt-4">
                    <h3 className="text-xl font-semibold tracking-tight transition-colors group-hover:text-primary line-clamp-1">{p.name}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground line-clamp-2">{p.description || "No description"}</p>
                  </div>
                  <div className="mt-auto pt-4 flex items-center gap-2 text-xs font-medium text-muted-foreground">
                    <span className="rounded-md bg-primary/10 px-2 py-1 text-primary">Project</span>
                    <span>Open to view boards</span>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        ) : projects === undefined ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex h-full min-h-[11rem] flex-col rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <Skeleton className="h-12 w-12 rounded-xl" />
                  <Skeleton className="h-5 w-20 rounded-full" />
                </div>
                <Skeleton className="mt-4 h-6 w-3/4" />
                <Skeleton className="mt-2 h-4 w-full" />
                <Skeleton className="mt-2 h-4 w-2/3" />
                <Skeleton className="mt-auto h-4 w-24" />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 p-8">
              <h3 className="mb-1 text-xl font-semibold">Welcome to your workspace! 🎉</h3>
              <p className="mb-6 text-sm text-muted-foreground">Follow these steps to get your team set up and running.</p>
              <div className="grid gap-4 sm:grid-cols-3">
                {[
                  { step: 1, icon: FolderOpen, title: "Create a project", desc: "Group related boards together inside a project.", action: isAdmin ? () => setOpen(true) : undefined, cta: "Create project" },
                  { step: 2, icon: Kanban, title: "Add a board", desc: "Create a board inside your project with columns and cards.", action: undefined, cta: "After step 1" },
                  { step: 3, icon: Users, title: "Invite your team", desc: "Add members to your workspace to collaborate in real time.", action: undefined, cta: "Go to Members", to: `/workspaces/${workspaceId}/members` },
                ].map(({ step, icon: Icon, title, desc, action, cta, to }) => (
                  <div key={step} className="flex flex-col rounded-xl border border-border bg-card p-5 shadow-sm">
                    <div className="mb-3 flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">{step}</div>
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <p className="mb-1 font-semibold">{title}</p>
                    <p className="mb-4 flex-1 text-xs text-muted-foreground">{desc}</p>
                    {action ? (
                      <Button size="sm" onClick={action} className="w-full">{cta} <ArrowRight className="ml-1 h-3.5 w-3.5" /></Button>
                    ) : to ? (
                      <Link to={to as any}><Button size="sm" variant="outline" className="w-full">{cta} <ArrowRight className="ml-1 h-3.5 w-3.5" /></Button></Link>
                    ) : (
                      <Button size="sm" variant="outline" className="w-full" disabled>{cta}</Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <form onSubmit={(e) => { e.preventDefault(); create.mutate({ workspaceId, name, description }) }} className="space-y-4">
          <DialogTitle>Create project</DialogTitle>
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div>
            <Label>Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} type="button">Cancel</Button>
            <Button type="submit" isLoading={create.isPending}>Create</Button>
          </div>
        </form>
      </Dialog>

      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)}>
        <form onSubmit={(e) => { e.preventDefault(); updateWorkspace.mutate({ name: editName, description: editDescription }) }} className="space-y-4">
          <DialogTitle>Workspace settings</DialogTitle>
          <div>
            <Label>Name</Label>
            <Input value={editName} onChange={(e) => setEditName(e.target.value)} required />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={editDescription} onChange={(e) => setEditDescription(e.target.value)} />
          </div>
          <div className="flex justify-between gap-2">
            <Button variant="destructive" type="button" onClick={() => confirm("Delete this workspace? This cannot be undone.") && deleteWorkspace.mutate()} isLoading={deleteWorkspace.isPending}>Delete workspace</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSettingsOpen(false)} type="button">Cancel</Button>
              <Button type="submit" isLoading={updateWorkspace.isPending}>Save</Button>
            </div>
          </div>
        </form>
      </Dialog>
    </Shell>
  )
}
